/* ==========================================================================
   Pulmoria theme — global behaviour
   Vanilla JS, custom elements. Written from scratch.
   ========================================================================== */

(function () {
  "use strict";

  const routes = window.Shopify && window.Shopify.routes ? window.Shopify.routes : { root: "/" };

  /* ---------------------------------------------------------------- helpers */
  const money = (cents) => {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.themeSettings && window.themeSettings.moneyFormat);
    }
    return "$" + (cents / 100).toFixed(2);
  };

  async function fetchCart() {
    const res = await fetch(`${routes.root}cart.js`, { headers: { Accept: "application/json" } });
    return res.json();
  }

  function publish(event, detail) {
    document.dispatchEvent(new CustomEvent(event, { detail }));
  }

  window.PulmoriaCart = { fetchCart, money };

  /* --------------------------------------------------------- Quantity input */
  class QuantitySelector extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="number"]');
      this.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-qty-change]");
        if (!btn || !this.input) return;
        const step = parseInt(btn.getAttribute("data-qty-change"), 10);
        const min = parseInt(this.input.min || "0", 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        let value = parseInt(this.input.value || "1", 10) + step;
        value = Math.max(min, Math.min(max, value));
        this.input.value = value;
        this.input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }
  }
  if (!customElements.get("quantity-selector")) customElements.define("quantity-selector", QuantitySelector);

  /* ------------------------------------------------------------- Cart drawer */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      this.querySelectorAll("[data-cart-close]").forEach((el) =>
        el.addEventListener("click", () => this.close())
      );
      document.addEventListener("cart:open", () => this.open());
      document.addEventListener("cart:updated", (e) => this.render(e.detail));
      this.addEventListener("click", (e) => {
        const remove = e.target.closest("[data-cart-remove]");
        if (remove) {
          e.preventDefault();
          this.changeItem(remove.getAttribute("data-cart-remove"), 0, remove.closest("[data-cart-item]"));
        }
      });
      this.addEventListener("change", (e) => {
        const input = e.target.closest("[data-cart-qty]");
        if (input) this.changeItem(input.getAttribute("data-cart-qty"), input.value, input.closest("[data-cart-item]"));
      });
    }
    open() {
      if (this.dialog && !this.dialog.open) {
        this.dialog.showModal();
        document.body.classList.add("overflow-hidden");
      }
    }
    close() {
      if (this.dialog && this.dialog.open) {
        this.dialog.close();
        document.body.classList.remove("overflow-hidden");
      }
    }
    async changeItem(line, quantity, itemEl) {
      if (itemEl) itemEl.classList.add("is-loading");
      this.setAttribute("loading", "");
      try {
        const res = await fetch(`${routes.root}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ line: parseInt(line, 10), quantity: parseInt(quantity, 10) }),
        });
        if (!res.ok) throw new Error("cart change failed");
        const cart = await res.json();
        publish("cart:updated", cart);
      } catch (err) {
        if (itemEl) itemEl.classList.remove("is-loading");
      } finally {
        this.removeAttribute("loading");
      }
    }
    async render(cart) {
      const c = cart || (await fetchCart());
      const container = this.querySelector("[data-cart-body]");
      if (container) {
        const res = await fetch(`${routes.root}?section_id=cart-drawer`);
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, "text/html");
        const fresh = html.querySelector("[data-cart-body]");
        if (fresh) container.innerHTML = fresh.innerHTML;
      }
      document.querySelectorAll("[data-cart-count]").forEach((el) => {
        el.textContent = c.item_count;
        el.toggleAttribute("hidden", c.item_count === 0);
      });
    }
  }
  if (!customElements.get("cart-drawer")) customElements.define("cart-drawer", CartDrawer);

  /* -------------------------------------------------------------- Add to cart */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      this.button = this.querySelector('[type="submit"]');
      if (this.form) this.form.addEventListener("submit", (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      const cartType = document.body.getAttribute("data-cart-type");
      if (cartType !== "drawer") return; // let default navigation happen
      e.preventDefault();
      if (this.button) {
        this.button.setAttribute("aria-busy", "true");
        this.button.classList.add("is-loading");
      }
      const formData = new FormData(this.form);
      formData.append("sections", "cart-drawer");
      try {
        const res = await fetch(`${routes.root}cart/add.js`, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });
        const data = await res.json();
        if (data.status) {
          this.showError(data.description || data.message);
        } else {
          const codeEl = this.querySelector("[data-discount-code]");
          const code = codeEl && codeEl.value ? codeEl.value.trim() : "";
          if (code) {
            try {
              await fetch(`${routes.root}discount/${encodeURIComponent(code)}?redirect=/cart.js`, {
                headers: { Accept: "application/json" },
              });
            } catch (e) {}
          }
          const cart = await fetchCart();
          publish("cart:updated", cart);
          publish("cart:open");
        }
      } catch (err) {
        this.showError("Something went wrong. Please try again.");
      } finally {
        if (this.button) {
          this.button.removeAttribute("aria-busy");
          this.button.classList.remove("is-loading");
        }
      }
    }
    showError(msg) {
      let el = this.querySelector("[data-form-error]");
      if (el) {
        el.textContent = msg;
        el.hidden = false;
      }
    }
  }
  if (!customElements.get("product-form")) customElements.define("product-form", ProductForm);

  /* --------------------------------------------------------- Variant picker */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.section = this.getAttribute("data-section");
      this.productUrl = this.getAttribute("data-url");
      this.variantsData = JSON.parse(this.querySelector('[type="application/json"]').textContent);
      this.addEventListener("change", () => this.onChange());
    }
    getSelectedOptions() {
      return Array.from(this.querySelectorAll("select, input:checked")).map((el) => el.value);
    }
    onChange() {
      const selected = this.getSelectedOptions();
      const variant = this.variantsData.find(
        (v) => JSON.stringify(v.options) === JSON.stringify(selected)
      );
      publish("variant:changed", { variant, section: this.section });
      this.updateUrl(variant);
      this.updateForm(variant);
    }
    updateUrl(variant) {
      if (!variant || !this.productUrl) return;
      window.history.replaceState({}, "", `${this.productUrl}?variant=${variant.id}`);
    }
    updateForm(variant) {
      const input = document.querySelector(`#variant-id-${this.section}`);
      if (input && variant) input.value = variant.id;
      const btn = document.querySelector(`#add-button-${this.section}`);
      if (btn) {
        const text = btn.querySelector("[data-add-text]");
        if (!variant) {
          btn.setAttribute("disabled", "");
          if (text) text.textContent = btn.getAttribute("data-unavailable") || "Unavailable";
        } else if (!variant.available) {
          btn.setAttribute("disabled", "");
          if (text) text.textContent = btn.getAttribute("data-soldout") || "Sold out";
        } else {
          btn.removeAttribute("disabled");
          if (text) text.textContent = btn.getAttribute("data-default") || "Add to cart";
        }
      }
    }
  }
  if (!customElements.get("variant-picker")) customElements.define("variant-picker", VariantPicker);

  /* ------------------------------------------------------------- Media gallery */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector("[data-gallery-main]");
      this.querySelectorAll("[data-gallery-thumb]").forEach((thumb) =>
        thumb.addEventListener("click", () => this.select(thumb.getAttribute("data-media-id")))
      );
      document.addEventListener("variant:changed", (e) => {
        if (e.detail.variant && e.detail.variant.featured_media) {
          this.select(String(e.detail.variant.featured_media.id));
        }
      });
    }
    select(id) {
      this.querySelectorAll("[data-media-item]").forEach((item) => {
        item.toggleAttribute("hidden", item.getAttribute("data-media-id") !== id);
      });
      this.querySelectorAll("[data-gallery-thumb]").forEach((t) =>
        t.toggleAttribute("aria-current", t.getAttribute("data-media-id") === id)
      );
    }
  }
  if (!customElements.get("media-gallery")) customElements.define("media-gallery", MediaGallery);

  /* --------------------------------------------------------- Mobile menu / drawer */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      document.querySelectorAll('[data-menu-open="' + this.id + '"]').forEach((btn) =>
        btn.addEventListener("click", () => this.dialog && this.dialog.showModal())
      );
      this.querySelectorAll("[data-menu-close]").forEach((btn) =>
        btn.addEventListener("click", () => this.dialog && this.dialog.close())
      );
    }
  }
  if (!customElements.get("menu-drawer")) customElements.define("menu-drawer", MenuDrawer);

  /* ---------------------------------------------------- Predictive search */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector("[data-search-results]");
      this.timer = null;
      if (this.input)
        this.input.addEventListener("input", () => {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.search(), 300);
        });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        if (this.results) this.results.innerHTML = "";
        return;
      }
      const url = `${routes.root}search/suggest?q=${encodeURIComponent(
        q
      )}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
      const res = await fetch(url);
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, "text/html");
      const inner = html.querySelector("[data-search-results]");
      if (this.results && inner) this.results.innerHTML = inner.innerHTML;
    }
  }
  if (!customElements.get("predictive-search")) customElements.define("predictive-search", PredictiveSearch);

  /* --------------------------------------------------------- Sticky header */
  const header = document.querySelector("[data-sticky-header]");
  if (header) {
    let last = 0;
    window.addEventListener(
      "scroll",
      () => {
        const y = window.scrollY;
        header.toggleAttribute("data-scrolled", y > 40);
        last = y;
      },
      { passive: true }
    );
  }
})();
